import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateNodeData } from '../../../../store/workflow/workflowSlice';
import type { RootState } from '../../../../store';
import { resolveEndpoint, resolveInputs } from '../../utils/transformLoad';
import FilePathWidget from './FilePathWidget';
import { isEnter } from '../../../../utils/keyboard';
import { useTranslation } from 'react-i18next';

interface Props { node: any }

/** 노드 유형 표시 이름 — 번역 키 (모듈 상수라 렌더에서 t() 로 바꾼다) */
export const ANALYTICS_LABEL_KEY: Record<string, string> = {
  aggregate: 'property:analytics.aggregate', sort: 'property:analytics.sort', lookup: 'property:analytics.lookup',
  filter: 'property:analytics.filter', join: 'property:analytics.join',
};
const JOIN_TYPES = ['INNER', 'LEFT', 'RIGHT', 'FULL'];
/** 조인 조건 'a.col = b.col' (또는 'col = col') → [왼쪽, 오른쪽] — 서버 join_rows 와 같은 읽기 */
const parseJoinKeys = (cond: string): [string, string] => {
  const m = /([\w.]+)\s*=+\s*([\w.]+)/.exec(cond || '');
  return m ? [m[1].split('.').pop() || '', m[2].split('.').pop() || ''] : ['', ''];
};
const AGG_FUNCS = ['sum', 'count', 'count_distinct', 'min', 'max', 'avg', 'first', 'last'];

/**
 * 필터 / 조인 / 집계 / 정렬·중복제거 / Lookup 노드 속성 — 한 모양으로(2026-10-20 필터·조인도 이리로).
 * 항목 후보는 선행(내부데이터/DB테이블/SAM파일)에서 가져오고, 없으면 직접 입력한다.
 * SAM파일·DB테이블에 바로 이어도 된다 — 실행할 때 그 원본을 직접 읽는다(서버 canvas_converter.DIRECT_READ_TYPES).
 */
export const inp = 'border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white';

/*
 * 아래 조각들은 **패널 밖**에 둔다. 패널 함수 안에서 만들면 글자를 하나 칠 때마다 새 컴포넌트가 되어
 * React 가 안에 든 입력칸을 통째로 다시 붙인다 — 글자를 지우면 커서가 사라지던 이유다.
 */
interface ColSelectProps { cols: string[]; value: string; onChange: (v: string) => void; allowEmpty?: boolean; placeholder?: string }
export const ColSelectBase: React.FC<ColSelectProps> = ({ cols, value, onChange, allowEmpty = true, placeholder }) => {
  const { t } = useTranslation();
  const ph = placeholder ?? t('property:an.columnPlaceholder');
  return cols.length ? (
    <select className={`${inp} w-full`} value={value || ''} onChange={(e) => onChange(e.target.value)}>
      {allowEmpty && <option value="">{ph}</option>}
      {cols.map((c) => <option key={c} value={c}>{c}</option>)}
      {value && !cols.includes(value) && <option value={value}>{value}</option>}
    </select>
  ) : <input className={`${inp} w-full font-mono`} value={value || ''} placeholder={ph} onChange={(e) => onChange(e.target.value)} />;
};

const Chips: React.FC<{ items: string[]; onRemove: (i: number) => void }> = ({ items, onRemove }) => (
  <div className="flex flex-wrap gap-1 mb-1">
    {items.map((c, i) => <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-xs text-gray-700 font-mono">{c}<button className="text-gray-400 hover:text-red-500" onClick={() => onRemove(i)}>✕</button></span>)}
  </div>
);

interface AddColProps { cols: string[]; onAdd: (c: string) => void; exclude?: string[] }
const AddColBase: React.FC<AddColProps> = ({ cols, onAdd, exclude = [] }) => {
  const { t } = useTranslation();
  const [manualCol, setManualCol] = useState('');      // 칸마다 따로 적는다 — 옆 칸에 친 글자가 따라오면 안 된다
  const add = (c: string) => { if (c.trim()) { onAdd(c.trim()); setManualCol(''); } };
  return (
    <div className="flex items-center gap-1">
      {cols.length ? (
        <select className={inp} value="" onChange={(e) => { if (e.target.value) onAdd(e.target.value); }}>
          <option value="">{t('property:an.addColumn')}</option>
          {cols.filter((c) => !exclude.includes(c)).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      ) : (
        <>
          <input className={`${inp} font-mono`} value={manualCol} placeholder={t('property:an.columnName')} onChange={(e) => setManualCol(e.target.value)} onKeyDown={(e) => { if (isEnter(e)) add(manualCol); }} />
          <button className="px-1.5 py-0.5 text-xs rounded border border-gray-300 hover:bg-gray-100" onClick={() => add(manualCol)}>{t('property:an.add')}</button>
        </>
      )}
    </div>
  );
};

export const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <div className="mb-3 rounded border border-gray-200 p-2"><div className="text-xs font-semibold text-gray-600 mb-1">{title}</div>{children}{hint && <div className="text-[0.6875rem] text-gray-400 mt-1">{hint}</div>}</div>
);

const AnalyticsNodePanel: React.FC<Props> = ({ node }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nodes = useSelector((s: any) => s.workflow.nodes);
  const edges = useSelector((s: any) => s.workflow.edges);
  const connections = useSelector((s: RootState) => s.connections.list);
  const cfg = node?.data?.config || {};
  const type = String(node.type);
  const patch = useCallback((p: Record<string, any>) => {
    dispatch(updateNodeData({ id: node.id, data: { config: { ...cfg, ...p }, ...(p.label !== undefined ? { label: p.label } : {}) } } as any));
  }, [cfg, dispatch, node.id]);

  const upstream = useMemo(() => resolveEndpoint(node.id, 'up', nodes, edges), [node.id, nodes, edges]);
  const cols = useMemo(() => (upstream?.columns || []).map((c) => c.name), [upstream]);
  // 조인 — 연결선마다의 입력(첫 번째 = 왼쪽, 두 번째 = 오른쪽)
  const inputs = useMemo(() => (type === 'join' ? resolveInputs(node.id, nodes, edges) : []), [type, node.id, nodes, edges]);
  const [leftKey, rightKey] = parseJoinKeys(cfg.condition || '');
  const sideCols = (i: number) => (inputs[i]?.columns || []).map((c) => c.name);

  const ColSelect = (pr: Omit<ColSelectProps, 'cols'>) => <ColSelectBase cols={cols} {...pr} />;
  const AddCol = (pr: Omit<AddColProps, 'cols'>) => <AddColBase cols={cols} {...pr} />;

  return (
    <div className="h-full bg-white p-4 overflow-auto">
      <div className="mb-3 pb-3 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{t('property:ui.properties')}</h3>
        <div className="flex items-center gap-2"><span className="text-xs font-semibold text-gray-500">{t('property:an.nodeType')}</span>
          <input type="text" value={ANALYTICS_LABEL_KEY[type] ? t(ANALYTICS_LABEL_KEY[type]) : type} readOnly className="text-xs text-gray-700 bg-gray-100 border-none p-1 rounded w-auto" style={{ minWidth: 80 }} /></div>
      </div>
      <div className="mb-3"><label className="block text-xs font-semibold text-gray-500 mb-1">{t('workflow:field.label')}</label>
        <input className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={cfg.label ?? node.data?.label ?? ''} onChange={(e) => patch({ label: e.target.value })} /></div>
      {type === 'join' ? (
        <div className="mb-3 text-[0.6875rem] text-gray-500 space-y-0.5">
          {[0, 1].map((i) => (
            <div key={i}>{t(i === 0 ? 'property:an.leftInput' : 'property:an.rightInput')}: {inputs[i]
              ? `${inputs[i].kindLabel} · ${inputs[i].name} (${t('property:an.colCount', { n: inputs[i].columns.length })})`
              : <span className="text-amber-600">{t('property:an.notConnected')}</span>}</div>
          ))}
          {inputs.length > 2 && <div className="text-amber-600">{t('property:an.joinTooMany', { n: inputs.length })}</div>}
        </div>
      ) : (
        <div className="mb-3 text-[0.6875rem] text-gray-500">{upstream ? t('property:an.inputFrom', { kind: upstream.kindLabel, name: upstream.name, n: cols.length }) : t('property:an.noUpstream')}</div>
      )}

      {type === 'filter' && (
        <Section title={t('property:an.filterCond')} hint={t('property:an.filterCondHint')}>
          <textarea className={`${inp} w-full font-mono`} rows={4} value={cfg.condition || ''} placeholder={t('property:an.filterCondPlaceholder')}
            onChange={(e) => patch({ condition: e.target.value })} />
          {cols.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1 items-center">
              <span className="text-[0.6875rem] text-gray-400">{t('property:an.insertColumn')}</span>
              {cols.map((c) => (
                <button key={c} type="button" className="px-1.5 py-0.5 rounded bg-gray-100 hover:bg-blue-100 text-xs text-gray-700 font-mono"
                  onClick={() => patch({ condition: `${(cfg.condition || '').replace(/\s*$/, '')}${cfg.condition ? ' ' : ''}${c} ` })}>{c}</button>
              ))}
            </div>
          )}
        </Section>
      )}

      {type === 'join' && (
        <>
          <Section title={t('property:an.joinType')} hint={t(`property:an.joinTypeHint.${(cfg.joinType || 'INNER').toUpperCase()}`)}>
            <select className={`${inp} w-full`} value={(cfg.joinType || 'INNER').toUpperCase()} onChange={(e) => patch({ joinType: e.target.value })}>
              {JOIN_TYPES.map((v) => <option key={v} value={v}>{t(`property:an.joinTypes.${v}`)}</option>)}
            </select>
          </Section>
          <Section title={t('property:an.joinKey')} hint={t('property:an.joinKeyHint')}>
            <div className="flex items-center gap-1">
              <div className="flex-1"><ColSelectBase cols={sideCols(0)} value={leftKey} placeholder={t('property:an.leftColumn')}
                onChange={(v) => patch({ condition: v || rightKey ? `${v} = ${rightKey || v}` : '' })} /></div>
              <span className="text-gray-400">=</span>
              <div className="flex-1"><ColSelectBase cols={sideCols(1)} value={rightKey} placeholder={t('property:an.rightColumn')}
                onChange={(v) => patch({ condition: v || leftKey ? `${leftKey || v} = ${v}` : '' })} /></div>
            </div>
          </Section>
        </>
      )}

      {type === 'aggregate' && (
        <>
          <Section title={t('property:an.groupBy')} hint={t('property:an.groupByHint')}>
            <Chips items={cfg.groupBy || []} onRemove={(i) => patch({ groupBy: (cfg.groupBy || []).filter((_: any, k: number) => k !== i) })} />
            <AddCol exclude={cfg.groupBy || []} onAdd={(c) => patch({ groupBy: [...(cfg.groupBy || []), c] })} />
          </Section>
          <Section title={t('property:an.aggs')} hint={t('property:an.aggsHint')}>
            <table className="w-full text-xs">
              <thead><tr className="text-left text-gray-500"><th className="py-0.5">{t('property:an.func')}</th><th className="py-0.5">{t('property:an.column')}</th><th className="py-0.5">{t('property:an.alias')}</th><th className="w-5"></th></tr></thead>
              <tbody>
                {(cfg.aggregations || []).map((a: any, i: number) => (
                  <tr key={i}>
                    <td className="py-0.5 pr-1"><select className={inp} value={a.func || 'sum'} onChange={(e) => patch({ aggregations: cfg.aggregations.map((x: any, k: number) => (k === i ? { ...x, func: e.target.value } : x)) })}>{AGG_FUNCS.map((v) => <option key={v} value={v}>{t(`property:agg.${v}`)}</option>)}</select></td>
                    <td className="py-0.5 pr-1"><ColSelect value={a.column || ''} onChange={(v) => patch({ aggregations: cfg.aggregations.map((x: any, k: number) => (k === i ? { ...x, column: v } : x)) })} /></td>
                    <td className="py-0.5 pr-1"><input className={`${inp} w-full font-mono`} value={a.alias || ''} placeholder={a.column ? `${a.func}_${a.column}` : a.func} onChange={(e) => patch({ aggregations: cfg.aggregations.map((x: any, k: number) => (k === i ? { ...x, alias: e.target.value } : x)) })} /></td>
                    <td><button className="text-gray-400 hover:text-red-500" onClick={() => patch({ aggregations: cfg.aggregations.filter((_: any, k: number) => k !== i) })}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="mt-1 px-2 py-0.5 text-xs rounded border border-gray-300 hover:bg-gray-100" onClick={() => patch({ aggregations: [...(cfg.aggregations || []), { func: 'sum', column: '', alias: '' }] })}>{t('property:an.addAgg')}</button>
          </Section>
        </>
      )}

      {type === 'sort' && (
        <>
          <Section title={t('property:an.sortKeys')} hint={t('property:an.sortKeysHint')}>
            {(cfg.sortKeys || []).map((k: any, i: number) => (
              <div key={i} className="flex items-center gap-1 mb-1">
                <div className="flex-1"><ColSelect value={k.column || ''} onChange={(v) => patch({ sortKeys: cfg.sortKeys.map((x: any, j: number) => (j === i ? { ...x, column: v } : x)) })} /></div>
                <select className={inp} value={k.desc ? 'desc' : 'asc'} onChange={(e) => patch({ sortKeys: cfg.sortKeys.map((x: any, j: number) => (j === i ? { ...x, desc: e.target.value === 'desc' } : x)) })}><option value="asc">{t('property:an.asc')}</option><option value="desc">{t('property:an.desc')}</option></select>
                <button className="text-gray-400 hover:text-red-500" onClick={() => patch({ sortKeys: cfg.sortKeys.filter((_: any, j: number) => j !== i) })}>✕</button>
              </div>
            ))}
            <button className="px-2 py-0.5 text-xs rounded border border-gray-300 hover:bg-gray-100" onClick={() => patch({ sortKeys: [...(cfg.sortKeys || []), { column: '', desc: false }] })}>{t('property:an.addSortKey')}</button>
          </Section>
          <Section title={t('property:an.dedupe')} hint={t('property:an.dedupeHint')}>
            <label className="flex items-center gap-1.5 text-xs text-gray-700 mb-1"><input type="checkbox" checked={!!cfg.distinct} onChange={(e) => patch({ distinct: e.target.checked })} />{t('property:an.dedupe')}</label>
            {cfg.distinct && (
              <>
                <Chips items={cfg.distinctKeys || []} onRemove={(i) => patch({ distinctKeys: (cfg.distinctKeys || []).filter((_: any, k: number) => k !== i) })} />
                <AddCol exclude={cfg.distinctKeys || []} onAdd={(c) => patch({ distinctKeys: [...(cfg.distinctKeys || []), c] })} />
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-700">{t('property:an.keepRow')}
                  <label className="flex items-center gap-1"><input type="radio" checked={(cfg.keep || 'first') === 'first'} onChange={() => patch({ keep: 'first' })} />{t('property:an.keepFirst')}</label>
                  <label className="flex items-center gap-1"><input type="radio" checked={cfg.keep === 'last'} onChange={() => patch({ keep: 'last' })} />{t('property:an.keepLast')}</label>
                </div>
              </>
            )}
          </Section>
        </>
      )}

      {type === 'lookup' && (
        <>
          <Section title={t('property:an.reference')} hint={t('property:an.referenceHint')}>
            <div className="flex items-center gap-3 text-xs text-gray-700 mb-1.5">
              <label className="flex items-center gap-1"><input type="radio" checked={(cfg.reference?.kind || 'db') === 'db'} onChange={() => patch({ reference: { ...(cfg.reference || {}), kind: 'db' } })} />{t('property:an.refDb')}</label>
              <label className="flex items-center gap-1"><input type="radio" checked={cfg.reference?.kind === 'csv'} onChange={() => patch({ reference: { ...(cfg.reference || {}), kind: 'csv' } })} />{t('property:an.refCsv')}</label>
            </div>
            {(cfg.reference?.kind || 'db') === 'db' ? (
              <div className="grid grid-cols-3 gap-1.5">
                <select className={inp} value={cfg.reference?.connection || ''} onChange={(e) => patch({ reference: { ...(cfg.reference || {}), kind: 'db', connection: e.target.value } })}>
                  <option value="">{t('property:an.connectionPlaceholder')}</option>{connections.map((c: any) => <option key={c.connection_id} value={c.connection_id}>{c.connection_id}</option>)}
                </select>
                {/* 커넥션은 접속 계정 — 어느 스키마의 테이블인지 함께 지정한다 */}
                <input className={`${inp} font-mono`} value={cfg.reference?.schema || ''} placeholder={t('property:an.schemaPlaceholder')} onChange={(e) => patch({ reference: { ...(cfg.reference || {}), kind: 'db', schema: e.target.value } })} />
                <input className={`${inp} font-mono`} value={cfg.reference?.table || ''} placeholder={t('property:an.tablePlaceholder')} onChange={(e) => patch({ reference: { ...(cfg.reference || {}), kind: 'db', table: e.target.value } })} />
              </div>
            ) : (
              <FilePathWidget id={`lk-${node.id}`} value={cfg.reference?.filepath || ''} onChange={(v: any) => patch({ reference: { ...(cfg.reference || {}), kind: 'csv', filepath: v || '' } })} />
            )}
          </Section>
          <Section title={t('property:an.keyMatch')}>
            {(cfg.keys || []).map((k: any, i: number) => (
              <div key={i} className="flex items-center gap-1 mb-1">
                <div className="flex-1"><ColSelect value={k.left || ''} onChange={(v) => patch({ keys: cfg.keys.map((x: any, j: number) => (j === i ? { ...x, left: v, right: x.right || v } : x)) })} placeholder={t('property:an.inputColumn')} /></div>
                <span className="text-gray-400">=</span>
                <input className={`${inp} flex-1 font-mono`} value={k.right || ''} placeholder={t('property:an.refColumn')} onChange={(e) => patch({ keys: cfg.keys.map((x: any, j: number) => (j === i ? { ...x, right: e.target.value } : x)) })} />
                <button className="text-gray-400 hover:text-red-500" onClick={() => patch({ keys: cfg.keys.filter((_: any, j: number) => j !== i) })}>✕</button>
              </div>
            ))}
            <button className="px-2 py-0.5 text-xs rounded border border-gray-300 hover:bg-gray-100" onClick={() => patch({ keys: [...(cfg.keys || []), { left: '', right: '' }] })}>{t('property:an.addKey')}</button>
          </Section>
          <Section title={t('property:an.fetchColumns')} hint={t('property:an.fetchColumnsHint')}>
            <input className={`${inp} w-full font-mono mb-1`} value={(cfg.columns || []).join(', ')} placeholder={t('property:an.fetchColumnsPlaceholder')} onChange={(e) => patch({ columns: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
            <div className="flex items-center gap-2 text-xs text-gray-700">
              <label className="flex items-center gap-1">{t('property:an.prefix')}<input className={`${inp} w-20 font-mono`} value={cfg.prefix || ''} onChange={(e) => patch({ prefix: e.target.value })} /></label>
              <label className="flex items-center gap-1 ml-2"><input type="checkbox" checked={cfg.unmatched === 'drop'} onChange={(e) => patch({ unmatched: e.target.checked ? 'drop' : 'keep' })} />{t('property:an.dropUnmatched')}</label>
            </div>
          </Section>
        </>
      )}
    </div>
  );
};

export default AnalyticsNodePanel;
